import { useEffect, useRef, useState } from 'react';
import {
  type Cart,
  disableCheckoutGCO,
  getPVBV,
  useTotalReducer,
  type CartEntryDetails,
  getActiveCart,
  type PromotionalItemOptions,
  CartTotalActionEnum,
  getDataForCartOverview,
  CartEvents,
  CartEventsEnum,
  CartEventsVersionsEnum,
  OrderType,
  getAllUnavailableEntries,
  isInactiveCart,
  displayPVBVValues,
  userNotRegistered,
  InitialCartState,
  SubCartDetail,
  includesAny,
  exceptionExist,
  getEKitTermsAndConditionsConfig,
} from '@commerce-cart-webcomponents/sdk';

import {
  DrawerDirections,
  useTranslation,
  Drawer,
  Button,
  ButtonSize,
  ButtonStyles,
  Theme,
  useSession,
  FAIcon,
  useNextgenRouter,
  ButtonWidth,
} from '@nextgen-web-framework/all';
import { formatPrice, formatNumber } from '@nextgen-web-framework/core';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { AccountProductType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProductType';
import { OrderSummaryLoader } from '../../common/components/cart-loader/order-summary-loader/OrderSummaryLoader';
import { CartAllSelectionCheckbox } from '../../common/components/cart-all-selection-checkbox/CartAllSelectionCheckbox';
import { useFeatureFlag } from '../../common';
import { CartOverview } from './cart-overview/CartOverview';
import styles from './CartTotal.module.scss';
import { ExpiryModal } from './expiry-modal/ExpiryModal';
import { PromoModal } from './promo-modal/PromoModal';
import { ItemsUnavailable } from './items-unavailable/ItemsUnavailable';

export interface CartTotalProps {
  data: Cart;
  clickCheckoutHandler: () => void;
  displayGCO?: boolean;
  displayCheckoutBtn: boolean;
  displayPVBV: boolean;
}

const {
  SET_DISPLAY,
  SET_DISABLE_CHECKOUT,
  SET_DISPLAY_UNAVAILABLE_MODAL,
  SET_UNAVAILABLE_ENTRIES,
  SET_DISPLAY_PROMO_MODAL,
} = CartTotalActionEnum;

export const CartTotal = (props: CartTotalProps) => {
  const { data, clickCheckoutHandler, displayGCO, displayCheckoutBtn, displayPVBV } = props;
  const notRegistered = userNotRegistered(data);

  const router = useNextgenRouter();
  const [showLoader, setShowLoader] = useState(false);
  const { hideBVFlag, cartSelectionFlag } = useFeatureFlag();

  const cartId = router.params?.cartId ?? '';
  const mainSubCart = getActiveCart(data, (cartId as string) ?? data.id);
  const dataForCartTotal = getDataForCartOverview(data, mainSubCart, displayCheckoutBtn);
  const { entries, promotionalItemOptions } = mainSubCart || ({} as SubCartDetail);
  const { isABO } = useSession();
  const t = useTranslation();
  const checkoutButtonText = notRegistered ? t('cart.cartTotal.registerAndCheckout') : t('cart.cartTotal.checkout');
  const allTextForCheckbox = t('cart.cartTotal.allTextForCheckbox');
  const noCartSelectedText = t('cart.cartTotal.noCartsSelected');
  const subscriptionIds = useRef<string[]>([]);
  const unavailableEntries = getAllUnavailableEntries(entries);
  const { isCheckoutDisabled }: InitialCartState = useSelector((state: AppState) => state.commerceCart);

  const {
    state,
    dispatch: totalDispatch,
    showOverview,
    handleShowDrawer,
    handlePromotionClick,
    handleCheckoutClick,
    handleReviewCartClick,
  } = useTotalReducer(
    data,
    entries as CartEntryDetails[],
    promotionalItemOptions as PromotionalItemOptions[],
    clickCheckoutHandler,
  );

  const hasEKitProduct = entries?.find((entry) => includesAny(entry, [AccountProductType.Ekit]));

  useEffect(() => {
    totalDispatch({ type: SET_DISPLAY, payload: false });
    if (data.orderType !== OrderType.Gco && entries?.length == 0) {
      totalDispatch({ type: SET_DISABLE_CHECKOUT, payload: true });
    } else {
      totalDispatch({ type: SET_DISABLE_CHECKOUT, payload: false });
    }

    totalDispatch({
      type: SET_UNAVAILABLE_ENTRIES,
      payload: unavailableEntries,
    });
  }, [entries, entries?.length, data]);

  useEffect(() => {
    const showLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.ShowLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowLoader(true);
      },
    });

    const hideLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.HideLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowLoader(false);
      },
    });

    subscriptionIds.current = [...hideLoaderEvent, ...showLoaderEvent];

    return () => {
      CartEvents.unsubscribe(subscriptionIds.current);
    };
  }, []);
  const selectedCartCount = data.subCarts.reduce((acc, { selected, totalEntries }) => {
    // checkout should be enabled when cart is disabled due to empty entries
    if (selected || (!selected && totalEntries === 0 && data.totalEntries !== 0)) {
      return acc + 1;
    }
    return acc;
  }, 0);
  const showTermsAndConditions = getEKitTermsAndConditionsConfig();
  const showCheckbox = cartSelectionFlag && displayGCO;
  const errorData = displayGCO ? data.errors : mainSubCart.errors;
  const isDisabled =
    isInactiveCart(displayGCO ? data.amwayUser : mainSubCart.amwayUser) ||
    state.disableCheckout ||
    (displayGCO ? disableCheckoutGCO(data) : false) ||
    Boolean(exceptionExist(errorData ?? [])) || //for all type of exceptions
    (hasEKitProduct && showTermsAndConditions && isCheckoutDisabled) ||
    (showCheckbox && selectedCartCount === 0);

  const showPVBV = displayPVBVValues(isABO(), displayPVBV, data);
  const { pointValue = 0, businessVolume = 0 } = getPVBV(data, mainSubCart, displayCheckoutBtn);

  const getPointValue = formatNumber(pointValue);
  const getBusinessVolume = formatNumber(businessVolume);

  // const amPoints = formatNumber(data?.totalAmPoint as number);

  //const showAmpoints = isMember() && Boolean(data?.totalAmPoint);
  const showCheckoutButton = () => (
    <div className={styles['cart-total-checkout-btn']}>
      <Button
        id="proceed-to-checkout-btn"
        onClick={() => handleCheckoutClick()}
        title={
          showCheckbox
            ? t('cart.cartTotal.selectedCartCheckout', {
                selectedCartCount: String(selectedCartCount),
              })
            : checkoutButtonText
        }
        tabIndex={0}
        buttonStyle={ButtonStyles.SOLID}
        theme={Theme.BLACK}
        size={ButtonSize.SMALL}
        disabled={isDisabled}
        data-testid="proceed-checkout-btn"
        aria-label={'proceed-checkout-btn'}
      />
    </div>
  );

  const showCartOverview = (isMobileDevice: boolean) => (
    <CartOverview
      cartData={data}
      data={dataForCartTotal}
      clickCheckoutHandler={isMobileDevice ? clickCheckoutHandler : () => handleCheckoutClick()}
      isDesktop={isMobileDevice ? false : true}
      displayCheckoutBtn={displayCheckoutBtn}
      displayGCO={displayGCO}
      displayPVBV={displayPVBV}
      checkoutBtnDisable={isDisabled}
      showChild={(val: boolean) => totalDispatch({ type: SET_DISPLAY, payload: val })}
    />
  );

  const showTotalPrice = () => (
    <span className={styles['price-cart-total']}>{formatPrice(Number(dataForCartTotal?.totalPrice?.value ?? 0))}</span>
  );

  const showTotalSectionCondition = (showCheckbox && selectedCartCount > 0) || !displayGCO || !cartSelectionFlag;

  return (
    <div className={styles['commerce-cart-total-container']}>
      <div className={styles['commerce-cart-total-container-mobile']}>
        {state.display && (
          <Drawer
            bottomGap={0}
            useReactPortal={true}
            portalContainerId={'react-portal-drawer'}
            footer={
              <div className={styles['cart-total-checkout-btn']}>
                <Button
                  id="proceed-to-checkout-btn-cart-total"
                  onClick={clickCheckoutHandler}
                  title={checkoutButtonText}
                  tabIndex={0}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  buttonStyle={ButtonStyles.SOLID}
                  theme={Theme.BLACK}
                  size={ButtonSize.MEDIUM}
                  role="button"
                  disabled={isDisabled}
                  data-testid="proceed-checkout-btn-cart-total"
                  aria-label="proceed-checkout-btn-cart-total"
                />
              </div>
            }
            direction={DrawerDirections.BOTTOM}
            onClose={() =>
              totalDispatch({
                type: SET_DISPLAY,
                payload: !state.display,
              })
            }
            closeIcon={true}
          >
            {showCartOverview(true)}
          </Drawer>
        )}
        {!state.display && (
          <>
            {showLoader ? (
              <OrderSummaryLoader />
            ) : (
              <>
                <div
                  className={styles['bottom-up-line']}
                  onClick={handleShowDrawer}
                  onKeyPress={handleShowDrawer}
                  role="presentation"
                  data-testid="cart-total-test-id"
                >
                  <span
                    className={entries?.length == 0 ? styles['disable-bar-bottom'] : styles['enable-bar-bottom']}
                  ></span>
                </div>
                <div className={styles['cart-total-section']}>
                  <div className={styles['cart-total-text-container-tab']}>
                    <div
                      role="presentation"
                      onClick={() => showOverview()}
                      onKeyPress={() => showOverview()}
                      data-testid="accordian-test-id"
                      className={styles['price_summary_accordion']}
                    >
                      <div className={styles['cart-total-text-container-tab-with-checkbox']}>
                        {showCheckbox && (
                          <span
                            aria-label="all-checkbox"
                            tabIndex={0}
                            role="button"
                            onKeyDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CartAllSelectionCheckbox cartData={data} text={allTextForCheckbox} />
                          </span>
                        )}
                        <div className={styles['cart-total-text-container-tab-with-checkbox-price-points']}>
                          <div className={styles['cart-total-text-container-tab-price-container']}>
                            <div className={styles['cart-total-text-container-tab-price-container-label']}>
                              {t('cart.cartTotal.total')}
                              <FAIcon onClick={showOverview} iconName="chevron-up" className={styles['chevron_icon']} />
                            </div>
                            <span className={styles['cart-total-text-container-tab-price-container-value']}>
                              {showTotalPrice()}
                            </span>
                          </div>
                          {showPVBV && (
                            <div className={styles['cart-total-text-container-tab-points-container']}>
                              <span className={styles['cart-total-text-container-tab-points-container-label']}>
                                {hideBVFlag ? t('cart.cartTotal.pvMobile') : t('cart.cartTotal.pvbvMobile')}
                              </span>
                              <span className={styles['cart-total-text-container-tab-points-container-value']}>
                                {`${String(getPointValue)} ${hideBVFlag ? '' : `/ ${String(getBusinessVolume)}`}`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <>{displayCheckoutBtn && showCheckoutButton()}</>
                  </div>
                </div>
                <div className={`${styles['cart-total-section-mobile']}`}>
                  <div className={styles['cart-total-text']}>
                    {showCheckbox && <CartAllSelectionCheckbox cartData={data} text={allTextForCheckbox} />}
                    {showTotalSectionCondition ? (
                      <div
                        onClick={() => showOverview()}
                        onKeyPress={() => showOverview()}
                        role="presentation"
                        data-testid="accordian-test-id-mobile"
                        className={styles['price_summary_accordion']}
                      >
                        <div className={styles['view_link_area']}>
                          <div className={styles['price-container']}>
                            {t('cart.cartTotal.total')}
                            <FAIcon onClick={showOverview} iconName="chevron-up" className={styles['chevron_icon']} />
                          </div>
                          {showTotalPrice()}
                        </div>
                        {showPVBV && (
                          <div className={styles['view_link_area_points']}>
                            <div className={styles['view_link_area_points_text']}>
                              {hideBVFlag ? t('cart.cartTotal.pvMobile') : t('cart.cartTotal.pvbvMobile')}
                            </div>
                            <span className={styles['view_link_area_points_value']}>
                              {`${String(getPointValue)} ${hideBVFlag ? '' : `/ ${String(getBusinessVolume)}`}`}
                            </span>
                          </div>
                        )}

                        {/* {showAmpoints && (
                        <div className={styles['view_link_area_points']}>
                          <div className={styles['view_link_area_points_text']}>{t('cart.cartTotal.APoints')}</div>
                          <span className={styles['view_link_area_points_value']}>{amPoints}</span>
                        </div>
                      )} */}
                      </div>
                    ) : (
                      <span className={styles['no-cart-selected-text']}>{noCartSelectedText}</span>
                    )}
                  </div>
                  {displayCheckoutBtn && showCheckoutButton()}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className={styles['commerce-cart-total-container-desktop']}>
        {showLoader ? <OrderSummaryLoader /> : showCartOverview(false)}
      </div>
      {/*Need to expiredProductsDataEntries to expired promotion product data*/}
      <ExpiryModal data={data} />
      {state.displayPromoModal && (
        <PromoModal
          onPrimaryClick={handlePromotionClick}
          onSecondaryClick={() => {
            clickCheckoutHandler();
            totalDispatch({
              type: SET_DISPLAY_PROMO_MODAL,
              payload: false,
            });
          }}
          onReviewCartClick={handleReviewCartClick}
        />
      )}
      {state.displayUnavailableModal && (
        <ItemsUnavailable
          notAvailableEntries={unavailableEntries}
          onPrimaryBtnClick={() =>
            totalDispatch({
              type: SET_DISPLAY_UNAVAILABLE_MODAL,
              payload: false,
            })
          }
        />
      )}
    </div>
  );
};
